import { useEffect, useRef } from 'react';

interface UseIntersectionObserverProps {
  threshold?: number;
  rootMargin?: string;
}

export const useIntersectionObserver = ({
  threshold = 0.1,
  rootMargin = '0px'
}: UseIntersectionObserverProps = {}) => {
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animated');
          }
        });
      },
      {
        threshold,
        rootMargin,
      }
    );

    // Add the initial class
    element.classList.add('animate-on-scroll');
    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [threshold, rootMargin]);

  return elementRef;
};

// Animated wrapper component
export const AnimatedSection: React.FC<{
  children: React.ReactNode;
  delay?: number;
  className?: string;
}> = ({ children, delay = 0, className = '' }) => {
  const ref = useIntersectionObserver();
  
  const delayClass = delay > 0 ? `delay-${Math.min(delay, 4)}` : '';
  
  return (
    <div 
      ref={ref} 
      className={`animate-on-scroll ${delayClass} ${className}`}
    >
      {children}
    </div>
  );
};